import React, { useRef, useState } from 'react';
import {
  Box,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  Typography,
} from '@mui/material';
import { ChevronRight, ExpandMoreRounded } from '@mui/icons-material';
import TranslateIcon from '@mui/icons-material/Translate';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { NavBarProps, NavGroupIcon, NavModule, NavSubmodule } from './types';
import { UNGROUPED_MODULE_ICON } from './navConfig';
import { isNewTabClick, toStandaloneModule } from './sideNavUtils';
import { ReactComponent as PanelLeftIcon } from '@assets/icons/panel-left-icon.svg';
import pvxLogo from '@assets/icons/pvx-logo.svg';
import UserLogoutPopover from './components/UserLogoutPopover';
import LanguageSelector from './components/LanguageSelector';
import { useTranslation } from 'react-i18next';
import BannerCarousel from '../Banner/BannerCarousel';
import TruncatingTypography from '../TruncatingTypography/TruncatingTypography';

const DesktopNavBar: React.FC<NavBarProps> = ({
  navGroups,
  ungroupedModules,
  openKeys,
  onToggleGroup,
  onModuleClick,
  onSubmoduleClick,
  isGroupActive,
  isModuleActive,
  isSubmoduleActive,
  userName,
  onClickLogout,
  customWidth,
  activeBanners,
}) => {
  const { t: translation } = useTranslation();

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isHoverExpanded, setIsHoverExpanded] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const drawerPaperRef = useRef<HTMLDivElement>(null);

  const isSidebarCollapsed = isCollapsed && !isHoverExpanded && !anchorEl;
  const [languageSelectorAnchorEl, setLanguageSelectorAnchorEl] = useState<null | HTMLElement>(
    null,
  );
  const isLanguageSelectorOpen = Boolean(languageSelectorAnchorEl);
  const handleSidebarMouseEnter = () => {
    if (isCollapsed) {
      setIsHoverExpanded(true);
    }
  };

  const handleSidebarMouseLeave = () => {
    setIsHoverExpanded(false);
  };

  const handleUserPopoverClose = (event?: { clientX?: number }) => {
    setAnchorEl(null);
    const sidebarRight = drawerPaperRef.current?.getBoundingClientRect().right ?? 0;
    setIsHoverExpanded((event?.clientX ?? Infinity) <= sidebarRight);
  };

  const handleSidebarToggle = () => {
    setIsCollapsed((previous) => !previous);
    setIsHoverExpanded(false);
  };

  const handleLinkClick = (event: React.MouseEvent, submodule: NavSubmodule) => {
    if (isNewTabClick(event)) return;
    event.preventDefault();
    onSubmoduleClick(submodule);
  };

  const getTopLevelRowSx = (isActive: boolean) => ({
    color: isActive ? '#162C36' : '#64748B',
    textDecoration: 'none',
    borderRadius: '8px',
    height: '36px',
    py: 0,
    px: isSidebarCollapsed ? 0 : '0.5rem',
    backgroundColor: isActive ? '#FAFBFC' : '#FFFFFF',

    justifyContent: isSidebarCollapsed ? 'center' : 'initial',
    '&:hover': {
      color: '#162C36',
      backgroundColor: '#FAFBFC',
    },
  });

  const renderTopLevelContent = (Icon: NavGroupIcon, label: string, isOpen = true) => (
    <>
      <Icon style={{ flexShrink: 0, height: '18px', width: '18px' }} />

      {!isSidebarCollapsed && (
        <>
          <TruncatingTypography
            sx={{ ml: 1.5, flex: 1, minWidth: 0 }}
            fontWeight={400}
            fontSize="1rem"
          >
            {label}
          </TruncatingTypography>

          {isOpen ? <></> : <ChevronRight sx={{ fontSize: 20, flexShrink: 0 }} />}
        </>
      )}
    </>
  );

  // Top-level row that expands its modules: a group, or an ungrouped nested module
  const renderExpandableRow = (
    key: string,
    Icon: NavGroupIcon,
    label: string,
    isActive: boolean,
    modules: NavModule[],
  ) => {
    const isOpen = openKeys.includes(key);

    return (
      <Box key={key}>
        {/* LEVEL 1: GROUP */}
        <ListItem onClick={() => onToggleGroup(key)} sx={getTopLevelRowSx(isActive)}>
          {renderTopLevelContent(Icon, label, isOpen)}
        </ListItem>

        {/* LEVEL 2: MODULES AND STANDALONE SUBMODULES */}
        {!isSidebarCollapsed && isOpen && (
          <List
            disablePadding
            sx={{ ml: 2, mr: 2, borderLeft: '1px solid #EBEEF1', pl: '0.75rem' }}
          >
            {modules.map(renderModule)}
          </List>
        )}
      </Box>
    );
  };

  const renderModule = (module: NavModule) => {
    const isNested = module.nested;
    const isActive = isModuleActive(module);
    const isModuleOpen = isNested && openKeys.includes(module.key);

    const rowSx = {
      borderRadius: '8px',
      py: 0,
      px: '8px',
      mb: '0.5rem',
      color: isActive ? '#162C36' : '#64748B',
      textDecoration: 'none',
      backgroundColor: isActive ? '#F5F7F8' : '#FFFFFF',

      '&:hover': {
        color: '#162C36',
        backgroundColor: '#F5F7F8',
      },
    };

    const rowContent = (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          width: '100%',
          minWidth: 0,
        }}
      >
        <TruncatingTypography
          sx={{ flex: 1, minWidth: 0, my: 0.5 }}
          fontWeight={!isNested && isActive ? 600 : 400}
          fontSize="1rem"
        >
          {module.label}
        </TruncatingTypography>

        {isNested &&
          (isModuleOpen ? (
            <ExpandMoreRounded sx={{ fontSize: 20, flexShrink: 0 }} />
          ) : (
            <ChevronRight sx={{ fontSize: 20, flexShrink: 0 }} />
          ))}
      </Box>
    );

    return (
      <Box key={module.key} sx={{ marginTop: '0.25rem' }}>
        {isNested ? (
          <ListItem onClick={() => onModuleClick(module)} sx={rowSx}>
            {rowContent}
          </ListItem>
        ) : (
          <ListItem
            component="a"
            href={module.submodules[0].href}
            onClick={(event) => handleLinkClick(event, module.submodules[0])}
            sx={rowSx}
          >
            {rowContent}
          </ListItem>
        )}

        {/* LEVEL 3: SUBMODULES */}
        {isModuleOpen && (
          <List
            disablePadding
            sx={{
              ml: 3,
              borderLeft: '1px solid #EBEEF1',
              pl: '0.5rem',
              pb: '8px',
            }}
          >
            {module.submodules.map((submodule) => {
              const isCurrentSubmodule = isSubmoduleActive(submodule);

              return (
                <ListItem
                  key={submodule.href}
                  component="a"
                  href={submodule.href}
                  onClick={(event) => handleLinkClick(event, submodule)}
                  sx={{
                    color: isCurrentSubmodule ? '#162C36' : '#64748B',
                    textDecoration: 'none',
                    backgroundColor: isCurrentSubmodule ? '#F5F7F8' : '#FFFFFF',
                    borderRadius: '8px',
                    mb: 0.5,
                    py: 0,
                    px: '8px',

                    '&:hover': {
                      color: '#162C36',
                      backgroundColor: '#F5F7F8',
                    },
                  }}
                >
                  <TruncatingTypography
                    sx={{ flex: 1, minWidth: 0, my: 0.5 }}
                    fontWeight={isCurrentSubmodule ? 600 : 400}
                    fontSize="1rem"
                  >
                    {submodule.label}
                  </TruncatingTypography>
                </ListItem>
              );
            })}
          </List>
        )}
      </Box>
    );
  };

  return (
    <>
      <Drawer
        variant="permanent"
        onMouseEnter={handleSidebarMouseEnter}
        onMouseLeave={handleSidebarMouseLeave}
        PaperProps={{ ref: drawerPaperRef }}
        sx={{
          height: '100%',
          width: isSidebarCollapsed ? '80px' : customWidth,
          maxWidth: '300px',
          flexShrink: 0,
          display: { xs: 'none', md: 'block' },
          position: 'relative',
          transition: 'width 0.3s ease-in-out',
          '& .MuiDrawer-paper': {
            overflowX: 'clip',
            width: isSidebarCollapsed ? '80px' : customWidth,
            transition: 'width 0.3s ease-in-out',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            background: '#FFFFFF',
            color: '#090D11',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: isSidebarCollapsed ? 'center' : 'space-between',
            alignItems: 'center',
            p: isSidebarCollapsed ? '0.5rem' : '1rem',
            gap: isSidebarCollapsed ? '8px' : '4px',
            flexDirection: isSidebarCollapsed ? 'column' : 'row',
            borderBottom: '1px solid #EBEEF1',
            height: '92px',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <img
              src={pvxLogo}
              alt="PvX"
              style={{
                height: '28px',
                width: '28px',
              }}
            />

            {!isSidebarCollapsed && (
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: '20px',
                  lineHeight: '22px',
                  color: '#090D11',
                  whiteSpace: 'nowrap',
                }}
              >
                PvX Partners
              </Typography>
            )}
          </Box>

          {!isSidebarCollapsed && (
            <Button
              sx={{
                minWidth: 0,
                width: isSidebarCollapsed ? '24px' : 'auto',
                height: isSidebarCollapsed ? '44px' : 'auto',
                p: 0,
              }}
              onClick={handleSidebarToggle}
            >
              <PanelLeftIcon style={{ height: '16px', width: '16px' }} />
            </Button>
          )}
        </Box>
        <Box
          id="scrollable-container"
          sx={{
            px: '8px',
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            mt: '8px',
          }}
        >
          {navGroups.length || ungroupedModules.length ? (
            <List
              sx={{
                py: 0,
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
              }}
            >
              {navGroups.map((group) =>
                renderExpandableRow(
                  group.key,
                  group.icon,
                  group.label,
                  isGroupActive(group),
                  group.modules,
                ),
              )}

              {/* Demo modules and backend modules missing from NAVIGATION_CONFIG */}
              {ungroupedModules.map((module) =>
                module.nested ? (
                  renderExpandableRow(
                    module.key,
                    UNGROUPED_MODULE_ICON,
                    module.label,
                    isModuleActive(module),
                    module.submodules.map(toStandaloneModule),
                  )
                ) : (
                  <ListItem
                    key={module.key}
                    component="a"
                    href={module.submodules[0].href}
                    onClick={(event) => handleLinkClick(event, module.submodules[0])}
                    sx={getTopLevelRowSx(isModuleActive(module))}
                  >
                    {renderTopLevelContent(UNGROUPED_MODULE_ICON, module.label)}
                  </ListItem>
                ),
              )}
            </List>
          ) : (
            Array.from({ length: 7 }).map((_, index) => (
              <Skeleton
                key={index}
                variant="rectangular"
                width="90%"
                height="4%"
                sx={{ marginTop: 2, borderRadius: 1, ml: 1 }}
              />
            ))
          )}
          {!isSidebarCollapsed && activeBanners && activeBanners.length > 0 && (
            <Box sx={{ marginTop: 'auto', flexShrink: 0 }}>
              <BannerCarousel banners={activeBanners} />
            </Box>
          )}
        </Box>
        <Box sx={{ width: '100%', flexShrink: 0, mt: '20px' }}>
          <Box
            sx={{
              display: 'flex',
              gap: '0.5rem',
              margin: '0.5rem',
              flexDirection: 'column',
            }}
          >
            {isSidebarCollapsed ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <TranslateIcon sx={{ fontSize: '1rem', color: '#64748B' }} />
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  paddingLeft: '0.5rem',
                  py: 0,
                  borderRadius: '8px',
                  backgroundColor: isLanguageSelectorOpen ? '#F5F7F8' : 'transparent',
                  color: isLanguageSelectorOpen ? '#162C36' : '#64748B',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: '#F5F7F8',
                    color: '#162C36',
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                >
                  <TranslateIcon sx={{ fontSize: '1.25rem', color: '#64748B' }} />
                  <Typography
                    fontSize="0.85rem"
                    fontWeight={isLanguageSelectorOpen ? 600 : 400}
                    lineHeight="1.25rem"
                    sx={{ color: isLanguageSelectorOpen ? '#162C36' : '#64748B' }}
                  >
                    {translation('signup.language')}
                  </Typography>
                </Box>
                <LanguageSelector
                  anchorEl={languageSelectorAnchorEl}
                  setAnchorEl={setLanguageSelectorAnchorEl}
                />
              </Box>
            )}
          </Box>
          {userName && (
            <>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '16px',
                  background: '#FFFFFF',
                  borderTop: '1px solid #EBEEF1',
                }}
              >
                {!isSidebarCollapsed ? (
                  <>
                    <ListItemText
                      primary={userName}
                      primaryTypographyProps={{
                        fontFamily: 'Manrope, sans-serif',
                        fontWeight: 500,
                        color: '#090D11',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '140px',
                      }}
                    />
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        width: 24,
                        height: 24,
                        cursor: 'pointer',
                      }}
                      onClick={(event: any) => {
                        setAnchorEl(event.currentTarget as HTMLElement);
                      }}
                    >
                      <MoreVertIcon sx={{ fontSize: '16px', color: '#090D11' }} />
                    </Box>
                  </>
                ) : (
                  <Box
                    sx={{
                      backgroundColor: '#C2E7E4',
                      border: '1px solid #E5E7EB',
                      borderRadius: '50%',
                      height: '32px',
                      width: '32px',
                      boxSizing: 'border-box',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '14px',
                        fontWeight: 500,
                        lineHeight: 1,
                        color: '#1B1C17',
                      }}
                    >
                      {userName?.trim().charAt(0).toUpperCase()}
                    </Typography>
                  </Box>
                )}
              </Box>
            </>
          )}
        </Box>
      </Drawer>
      {anchorEl && (
        <UserLogoutPopover
          handlePopoverClose={handleUserPopoverClose}
          logoutHandler={onClickLogout}
          anchorEl={anchorEl}
        />
      )}
    </>
  );
};
export default DesktopNavBar;
